"use client";

import { useEffect, useRef } from "react";
import posthog from "posthog-js";
import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import { useTranslation } from "@/lib/i18n/context";
import { type ConsentRegion } from "@/lib/consent/regions";
import { getConsentProfile } from "@/lib/consent/profiles";
import { CONSENT_VERSION, CONSENT_COOKIE_NAME, REGION_COOKIE_NAME, CATEGORY_KEYS } from "@/lib/consent/version";
import { logConsent } from "@/lib/consent/log";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    openCookiePreferences?: () => void;
    triggerDoNotSell?: () => void;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[$()*+./?[\\\]^{|}]/g, "\\$&") + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

function pushConsentMode(categories: Record<string, boolean>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  window.gtag("consent", "update", {
    ad_storage: categories.marketing ? "granted" : "denied",
    ad_user_data: categories.marketing ? "granted" : "denied",
    ad_personalization: categories.marketing ? "granted" : "denied",
    analytics_storage: categories.analytics ? "granted" : "denied",
    // theme/lang/timezone are user-explicit prefs — covered by Art. 5(3) ePrivacy strictly-necessary exception.
    functionality_storage: "granted",
    personalization_storage: "granted",
    security_storage: "granted",
  });

  // PostHog (client) is opt-out by default (see instrumentation-client.ts) — flip
  // it in step with the analytics category so it only captures/sets cookies after
  // consent. Server-side PostHog events run under legitimate interest separately.
  try {
    if (categories.analytics) posthog.opt_in_capturing();
    else posthog.opt_out_capturing();
  } catch {
    /* posthog not ready yet — instrumentation-client init will apply defaults */
  }
}

export function CookieConsentBanner() {
  const { t, lang } = useTranslation();
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const region = (readCookie(REGION_COOKIE_NAME) as ConsentRegion) || "ROW";
    const gpc = typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
    const profile = getConsentProfile(region, gpc);

    const tBanner = t.consent.banner;
    const tCats = t.consent.categories;

    const langKey = lang === "en" ? "en" : "sl";

    const buildCategoriesUI = () => ({
      necessary: {
        title: tCats.necessary.title,
        description: tCats.necessary.description,
        linkedCategory: "necessary",
      },
      analytics: {
        title: tCats.analytics.title,
        description: tCats.analytics.description,
        linkedCategory: "analytics",
      },
      marketing: {
        title: tCats.marketing.title,
        description: tCats.marketing.description,
        linkedCategory: "marketing",
      },
    });

    CookieConsent.run({
      cookie: {
        name: CONSENT_COOKIE_NAME,
        expiresAfterDays: 182,
        sameSite: "Lax",
      },
      revision: 1,
      guiOptions: {
        consentModal: {
          layout: profile.blockUntilDecision ? "box" : "cloud",
          position: profile.blockUntilDecision ? "middle center" : "bottom right",
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: "box",
          position: "right",
          equalWeightButtons: true,
          flipButtons: false,
        },
      },
      disablePageInteraction: profile.blockUntilDecision,
      categories: {
        necessary: { enabled: true, readOnly: true },
        analytics: { enabled: profile.defaults.analytics },
        marketing: { enabled: profile.defaults.marketing },
      },
      language: {
        default: langKey,
        translations: {
          [langKey]: {
            consentModal: {
              title: profile.mode === "opt-out" ? tBanner.noticeTitle : tBanner.title,
              description: profile.mode === "opt-out" ? tBanner.noticeDescription : tBanner.description,
              acceptAllBtn: tBanner.acceptAll,
              acceptNecessaryBtn: tBanner.rejectAll,
              showPreferencesBtn: tBanner.customize,
              footer: `<a href="/privacy">${t.consent.footer.privacy}</a> | <a href="/cookies">${t.consent.footer.cookies}</a>`,
            },
            preferencesModal: {
              title: tBanner.title,
              acceptAllBtn: tBanner.acceptAll,
              acceptNecessaryBtn: tBanner.rejectAll,
              savePreferencesBtn: tBanner.saveSelection,
              closeIconLabel: t.common.close ?? "Close",
              sections: Object.entries(buildCategoriesUI()).map(([, v]) => ({
                title: v.title,
                description: v.description,
                linkedCategory: v.linkedCategory,
              })),
            },
          },
        },
      },
      onFirstConsent: ({ cookie }) => {
        const cats = readCategories(cookie);
        pushConsentMode(cats);
        void logConsent({
          eventType: "first_consent",
          categories: cats as Record<(typeof CATEGORY_KEYS)[number], boolean>,
          region,
          gpcSignal: gpc,
          consentVersion: CONSENT_VERSION,
        });
      },
      onChange: ({ cookie }) => {
        const cats = readCategories(cookie);
        pushConsentMode(cats);
        void logConsent({
          eventType: "update",
          categories: cats as Record<(typeof CATEGORY_KEYS)[number], boolean>,
          region,
          gpcSignal: gpc,
          consentVersion: CONSENT_VERSION,
        });
      },
      onConsent: ({ cookie }) => {
        // Replays existing consent on every page load — sync GCM only, no log spam.
        const cats = readCategories(cookie);
        pushConsentMode(cats);
      },
    });

    window.openCookiePreferences = () => CookieConsent.showPreferences();
    window.triggerDoNotSell = () => {
      CookieConsent.acceptCategory(["necessary"]);
      void logConsent({
        eventType: "do_not_sell",
        categories: { necessary: true, analytics: false, marketing: false },
        region,
        gpcSignal: gpc,
        consentVersion: CONSENT_VERSION,
      });
    };

    if (profile.respectGPC && gpc) {
      // GPC = automatic opt-out for analytics/marketing, no banner action required.
      CookieConsent.acceptCategory(["necessary"]);
    }
  }, [t, lang]);

  return null;
}

function readCategories(cookie: { categories?: string[] } | undefined): Record<string, boolean> {
  const accepted = new Set(cookie?.categories || []);
  return {
    necessary: true,
    analytics: accepted.has("analytics"),
    marketing: accepted.has("marketing"),
  };
}

export function openCookiePreferences() {
  if (typeof window !== "undefined" && window.openCookiePreferences) {
    window.openCookiePreferences();
  }
}

export function triggerDoNotSell() {
  if (typeof window !== "undefined" && window.triggerDoNotSell) {
    window.triggerDoNotSell();
  }
}
